import React from 'react';

// Port of the iOS prominent-colour algorithm.
// 1. Count every non-transparent pixel by exact RGB (no quantisation).
// 2. Keep only colours whose count is > half the most-common count.
// 3. Among those candidates, pick the one with the highest chroma that isn't
//    near-black, near-white, or near-grey.
// 4. If no colourful candidate survives, fall back to the single most-common colour.
export default function useProminentColor(imageUrl) {
  const [color, setColor] = React.useState(null);

  React.useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const scale = Math.min(1, 200 / Math.max(img.naturalWidth, img.naturalHeight, 1));
        const w = Math.round(img.naturalWidth * scale);
        const h = Math.round(img.naturalHeight * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);

        const colorCounts = new Map();
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] === 0) continue;
          const key = (data[i] << 16) | (data[i + 1] << 8) | data[i + 2];
          colorCounts.set(key, (colorCounts.get(key) || 0) + 1);
        }
        if (colorCounts.size === 0) return;

        const sorted = [...colorCounts.entries()].sort(
          ([ka, va], [kb, vb]) => vb !== va ? vb - va : kb - ka
        );
        const maxCount = sorted[0][1];
        const candidates = sorted.filter(([, v]) => v > maxCount / 2);

        const isNearBlackOrWhite = (r, g, b) =>
          (r < 30 && g < 30 && b < 30) || (r > 225 && g > 225 && b > 225);
        const isNearGray = (r, g, b) =>
          Math.max(r, g, b) - Math.min(r, g, b) < 30;

        let bestKey = null, bestChroma = -1;
        for (const [key] of candidates) {
          const r = (key >> 16) & 0xFF, g = (key >> 8) & 0xFF, b = key & 0xFF;
          if (isNearBlackOrWhite(r, g, b) || isNearGray(r, g, b)) continue;
          const chroma = Math.max(r, g, b) - Math.min(r, g, b);
          if (chroma > bestChroma) { bestChroma = chroma; bestKey = key; }
        }

        const result = bestKey ?? sorted[0][0];
        const r = (result >> 16) & 0xFF, g = (result >> 8) & 0xFF, b = result & 0xFF;
        const hex = v => v.toString(16).padStart(2, '0');
        setColor(`#${hex(r)}${hex(g)}${hex(b)}`);
      } catch {
        setColor(null);
      }
    };
    img.onerror = () => setColor(null);
    img.src = imageUrl;
  }, [imageUrl]);

  return color;
}
